import debounce from 'lodash/debounce'
import { useCallback, useEffect, useRef, useState } from 'react'
import { merchantAxios } from '../api'
import {
  getListObject,
  getObject,
  getSignedUrl,
  putObject,
  sendMessage,
  getSSM
} from '../api/s3Api'

export const saveS3 = async (files, token, orgName, bucket = 'qallix-public', fullPath = true, formatKey = true) => {
  if (!Array.isArray(files)) files = [files]

  return new Promise(async (resolve, reject) => {
    try {
      const results = await Promise.all(
        files.map(async (file) => {
          const base64String = await toBase64(file.body)
          const fileKey = formatKey ? formatFileKey(file) : file.key

          const payload = {
            putObjectItems: [
              {
                bucketName: bucket,
                contentType: file.body.type,
                key: fileKey,
                fileContent: base64String,
              },
            ],
            organization: orgName,
          }

          await putObject(token, payload)

          return fullPath ? `https://${bucket}.s3.ap-southeast-1.amazonaws.com/${fileKey}` : fileKey
        }),
      )

      resolve(results)
    } catch (e) {
      reject(e)
    }
  })
}

export const getParameterFromSSM = async (token, orgName) => {
  try {
    const response = await getSSM(token, {
      organization: orgName,
    })
    return response.key
  } catch (error) {
    console.error(`Error retrieving parameter: ${error}`)
    throw error
  }
}



export const getS3Object = (key, bucket = 'qallix-storage', token = null, orgName = null) => {
  return new Promise(async (resolve, reject) => {
    const response = await getObject(token, {
      organization: orgName,
      bucketName: bucket,
      key: key,
    })
    resolve(response)
  })
}

export const createDocument = async (
  key,
  token = null,
  orgName = null,
  bucket = 'qallix-storage',
) => {
  let type
  const docType = key.split('.').pop()
  if (docType === 'pdf') {
    type = 'application/pdf'
  } else if (docType === 'html') {
    type = 'text/html'
  } else if (docType === 'jpg' || docType === 'jpeg') {
    type = 'image/jpeg'
  } else {
    throw `Invalid document type: ${docType}`
  }

  const response = await getObject(token, {
    organization: orgName,
    bucketName: bucket,
    key: key,
  })

  const base64String = response.fileContent.toString('base64')

  const byteCharacters = atob(base64String)
  const byteNumbers = new Array(byteCharacters.length)
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i)
  }
  const byteArray = new Uint8Array(byteNumbers)
  const blob = new Blob([byteArray], { type: type })
  const pdfUrl = URL.createObjectURL(blob)

  let success = window.open(pdfUrl, '_blank')
  if (!success) {
    window.alert(
      'Popup was blocked.\nPlease enable pop-ups using the button to the right of the website URL.\nAfter enabling, the document will open after closing this message.',
    )
    window.open(pdfUrl, '_blank')
  }
}

export const downloadS3File = async (
  key,
  token = null,
  orgName = null,
  bucket = 'qallix-storage',
) => {
  const response = await getObject(token, {
    organization: orgName,
    bucketName: bucket,
    key: key,
  })

  let fileBuffer;
  if (typeof response.fileContent === 'string') {
    fileBuffer = base64ToArrayBuffer(response.fileContent);
  } else {
    fileBuffer = response.fileContent;
  }

  const blob = new Blob([fileBuffer], {
    type: response.contentType,
  })

  const url = URL.createObjectURL(blob)

  const link = document.createElement('a')
  link.href = url
  link.setAttribute('download', key.split('/').pop())

  document.body.appendChild(link)
  link.click()

  link.parentNode.removeChild(link)
  URL.revokeObjectURL(url)
}

function base64ToArrayBuffer(base64) {
  const binary_string = atob(base64);
  const len = binary_string.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    bytes[i] = binary_string.charCodeAt(i);
  }
  return bytes.buffer;
}


const toBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.readAsDataURL(file)
    reader.onload = () => {
      const [, base64] = reader.result.split(',')
      resolve(base64)
    }
    reader.onerror = reject
  })
}

const formatFileKey = (file) => {
  const subtype = file.body.type.split('/')[1].split('+')[0]
  const dotExt = `.${subtype}`
  let fileKey = file.key

  if (!fileKey.endsWith(dotExt)) {
    fileKey =
      fileKey.endsWith(subtype) ? fileKey.slice(0, -subtype.length) + dotExt : fileKey + dotExt
  }

  return fileKey
}


export const sendSNSMessage = async (
  message,
  topic = 'console-alerts',
  subject = `${(process.env.REACT_APP_BUILD_ENV || 'development') === 'development' ? 'dev-' : ''}console-qallix Encountered an Issue`,
  token = null,
  orgName = null,
) => {
  try {
    await sendMessage(token, {
      organization: orgName,
      topic,
      message,
      subject,
    })
  } catch (error) {
    console.error(`Error sending SNS message: ${error}`)
  }
}